// ⌘K: jump to anything, create anything, or log an entry against anything without
// leaving the keyboard. Latency-to-capture is most of what "power user" means, so
// this is the app's spine rather than a search box bolted on.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { todayIso } from '../lib/format'
import { newId } from '../lib/ids'
import {
  accentVar,
  KIND_LIST,
  KINDS,
  labelOf,
  titleField,
  type Config,
  type EntityKind,
} from '../lib/model'
import { entitiesOfKind, fieldStr, stageOf, titleOf, type EntityRecord } from '../lib/reducer'
import { useStore } from '../lib/store'
import { Icon, iconName, type IconName } from './Icon'
import { defaultsFor } from './useCreate'

interface Item {
  key: string
  label: string
  icon: IconName
  sub?: string
  /** CSS var name for the kind's colour */
  hue?: string
  /** a heading rendered above the first item carrying it */
  group?: string
  run: () => void
}

/**
 * The words a record can be found by, beyond its name: its kind, and whatever label
 * puts it in a category — a work's kind, a deal's stage, a person's role. Typing "demo" or "won" should list everything in that category,
 * which is a different question from "find the thing called X" and just as common.
 */
function facetsOf(rec: EntityRecord, config: Config): string[] {
  const out = [KINDS[rec.kind].singular]
  switch (rec.kind) {
    case 'project':
      out.push(labelOf(config.projectKinds, fieldStr(rec, 'kind'))?.label ?? '')
      break
    case 'deal':
      out.push(config.stages.find((s) => s.key === stageOf(rec, config.stages))?.label ?? '')
      break
    case 'org':
      out.push(fieldStr(rec, 'location'))
      break
    case 'person':
      out.push(fieldStr(rec, 'role'))
      break
  }
  return out.filter(Boolean)
}

/** What the bar is doing: picking, naming a new record, or writing an entry. */
type Mode =
  | { kind: 'browse' }
  | { kind: 'create'; entity: EntityKind }
  | { kind: 'log'; subject: string }

export function CommandBar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { state, dispatch, config, version } = useStore()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [mode, setMode] = useState<Mode>({ kind: 'browse' })
  const [active, setActive] = useState(0)

  useEffect(() => {
    if (open) {
      setQ('')
      setMode({ kind: 'browse' })
      setActive(0)
    }
  }, [open])

  async function create(entity: EntityKind, name: string) {
    const clean = name.trim()
    if (!clean) return
    const id = newId(KINDS[entity].prefix)
    await dispatch({
      type: `${entity}.create`,
      subject: id,
      data: {
        [titleField(entity)]: clean,
        ...defaultsFor(entity, config.stages, config.projectKinds),
      },
    })
    onClose()
    navigate(`/e/${id}`)
  }

  function logEntry(subject: string, body: string) {
    if (!body.trim()) return
    void dispatch({
      type: 'entry.create',
      subject: newId('ent'),
      data: { subject, body, kind: 'note', source: 'human', occurredAt: todayIso() },
    })
    onClose()
    navigate(`/e/${subject}`)
  }

  const items = useMemo<Item[]>(() => {
    void version
    if (mode.kind !== 'browse') return []
    const ql = q.trim().toLowerCase()

    const all = KIND_LIST.flatMap((k) => entitiesOfKind(state, k.kind))
    const rowFor = (rec: EntityRecord, group?: string): Item => {
      const facets = facetsOf(rec, config)
      return {
        key: (group ?? '') + rec.id,
        label: titleOf(state, rec.id) || `Untitled ${KINDS[rec.kind].singular.toLowerCase()}`,
        icon: iconName(KINDS[rec.kind].icon),
        // The category rides along on the row, so a facet match explains itself.
        sub: facets.slice(0, 2).join(' · '),
        hue: accentVar(rec.kind),
        ...(group ? { group } : {}),
        run: () => {
          onClose()
          navigate(`/e/${rec.id}`)
        },
      }
    }

    // ── nothing typed: what you'd want without asking ────────────────────────
    if (ql === '') {
      const creates: Item[] = KIND_LIST.map((k, i) => ({
        key: `new-${k.kind}`,
        label: `New ${k.singular.toLowerCase()}`,
        icon: iconName(k.icon),
        hue: accentVar(k.kind),
        ...(i === 0 ? { group: 'Create' } : {}),
        run: () => {
          setMode({ kind: 'create', entity: k.kind })
          setActive(0)
        },
      }))
      const recent = [...all]
        .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
        .slice(0, 8)
        .map((rec, i) => rowFor(rec, i === 0 ? 'Recently added' : undefined))
      return [...creates, ...recent]
    }

    // ── typed: match the name OR the category ────────────────────────────────
    const matches = all
      .map((rec) => ({
        rec,
        hay: [titleOf(state, rec.id), ...facetsOf(rec, config)].join(' ').toLowerCase(),
      }))
      .filter((x) => x.hay.includes(ql))
      .slice(0, 9)
      .map(({ rec }, i) => rowFor(rec, i === 0 ? 'Matches' : undefined))

    const logTargets: Item[] =
      ql.length >= 2
        ? all
            .filter((rec) => titleOf(state, rec.id).toLowerCase().includes(ql))
            .slice(0, 2)
            .map((rec, i) => ({
              key: `log-${rec.id}`,
              label: `Log an entry on ${titleOf(state, rec.id)}`,
              icon: 'MessageSquare' as IconName,
              ...(i === 0 ? { group: 'Write' } : {}),
              run: () => {
                setMode({ kind: 'log', subject: rec.id })
                setQ('')
                setActive(0)
              },
            }))
        : []

    const creates: Item[] = KIND_LIST.map((k, i) => ({
      key: `new-${k.kind}`,
      label: `New ${k.singular.toLowerCase()} “${q.trim()}”`,
      icon: iconName(k.icon),
      hue: accentVar(k.kind),
      ...(i === 0 ? { group: 'Create' } : {}),
      run: () => void create(k.kind, q),
    }))

    return [...matches, ...logTargets, ...creates]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, mode, version, config, navigate])

  if (!open) return null

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') return onClose()
    if (mode.kind === 'create') {
      if (e.key === 'Enter') {
        e.preventDefault()
        void create(mode.entity, q)
      }
      if (e.key === 'Backspace' && q === '') setMode({ kind: 'browse' })
      return
    }
    if (mode.kind === 'log') {
      if (e.key === 'Enter') {
        e.preventDefault()
        logEntry(mode.subject, q)
      }
      if (e.key === 'Backspace' && q === '') setMode({ kind: 'browse' })
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => Math.min(a + 1, items.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(a - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      items[active]?.run()
    }
  }

  const chip =
    mode.kind === 'create'
      ? {
          label: `New ${KINDS[mode.entity].singular.toLowerCase()}`,
          icon: iconName(KINDS[mode.entity].icon),
        }
      : mode.kind === 'log'
        ? {
            label: `Entry on ${titleOf(state, mode.subject) || 'record'}`,
            icon: 'MessageSquare' as IconName,
          }
        : null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[14vh]"
      onClick={onClose}
      style={{ background: 'rgba(0,0,0,0.32)' }}
    >
      <div
        className="rounded-float w-full max-w-lg overflow-hidden border shadow-2xl"
        style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center gap-2 border-b px-3"
          style={{ borderColor: 'var(--border)' }}
        >
          {chip ? (
            <span
              className="rounded-control my-2 flex items-center gap-1.5 px-2 py-1"
              style={{
                background: 'var(--work-soft)',
                color: 'var(--marine)',
                fontFamily: 'var(--mono)',
                fontSize: 'var(--text-micro)',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              <Icon name={chip.icon} size={12} /> {chip.label}
            </span>
          ) : (
            <Icon name="Search" size={16} style={{ color: 'var(--faint)' }} />
          )}
          <input
            autoFocus
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setActive(0)
            }}
            onKeyDown={onKeyDown}
            placeholder={
              mode.kind === 'create'
                ? `${KINDS[mode.entity].singular} name…`
                : mode.kind === 'log'
                  ? 'What happened?'
                  : 'Jump to anything, or type a name to create it…'
            }
            className="no-ring text-body flex-1 bg-transparent py-3 outline-none"
          />
          {mode.kind !== 'browse' && <span className="meta">Enter to save</span>}
        </div>

        {mode.kind === 'browse' && (
          <ul className="max-h-80 overflow-auto py-1" style={{ overscrollBehavior: 'contain' }}>
            {items.length === 0 && (
              <li className="text-body px-3 py-3" style={{ color: 'var(--muted)' }}>
                Nothing matches. Try a category: “demo”, “won”, “artifact”.
              </li>
            )}
            {items.map((it, i) => (
              <li key={it.key}>
                {it.group && <div className="eyebrow px-3 pt-2 pb-1">{it.group}</div>}
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={it.run}
                  className="text-body flex w-full items-center gap-2.5 px-3 py-2 text-left"
                  style={{ background: i === active ? 'var(--hover)' : 'transparent' }}
                >
                  <Icon
                    name={it.icon}
                    size={14}
                    style={{ color: it.hue ? `var(${it.hue})` : 'var(--faint)' }}
                  />
                  <span className="flex-1 truncate">{it.label}</span>
                  {it.sub && <span className="eyebrow">{it.sub}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
